import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged, User, browserLocalPersistence, setPersistence, getRedirectResult } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import IndependentLogin from '../../authentication/IndependentLogin';
import WorkspaceLayout from './WorkspaceLayout';
import IndependentDashboard from './IndependentDashboard';
import LessonPlannerView from './LessonPlannerView';
import StudentManagerView from './StudentManagerView';
import IndependentResourcesView from './IndependentResourcesView';
import IndependentResearchView from './IndependentResearchView';
import IndependentAssessmentView from './IndependentAssessmentView';
import IndependentUtilitiesView from './IndependentUtilitiesView';
import IndependentPDFConverterView from './IndependentPDFConverterView';
import IndependentCalculatorView from './IndependentCalculatorView';
import IndependentAICalculatorView from './IndependentAICalculatorView';
import IndependentSearchUtility from './IndependentSearchUtility';
import TeacherLanding from './TeacherLanding';
import PageLoader from '../../authentication/PageLoader';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { recaptchaKey } from '../../firebase/config';
import { AnimatePresence, motion } from 'framer-motion';
import LessonCreateView from './LessonCreateView';
import LessonReadView from './LessonReadView';
import AddCourseView from './AddCourseView';
import StudentAssessmentLayout from '../../StudentAssessment/StudentAssessmentLayout';
import StudentLanding from '../../StudentAssessment/StudentLanding';
import AssessmentRunner from '../../StudentAssessment/AssessmentRunner';
import CourseOutlineWriter from './CourseOutlineWriter';
import CourseOutlineEditor from './CourseOutlineEditor';
import SubTopicContentViewer from './SubTopicContentViewer';
import CourseMaterialsView from './CourseMaterialsView';
import IndependentBillingView from './IndependentBillingView';
import IndependentAccountView from './IndependentAccountView';
import ClassesManagerView from './ClassesManagerView';
import CreatePDFView from './CreatePDFView';
import PrivacyPolicy from '../../components/requiredlegaldocuments/PrivacyPolicy';
import TermsOfService from '../../components/requiredlegaldocuments/TermsOfService';


// Auth persistence key — lightweight UID hint stored in localStorage
const TEACH_AUTH_HINT = 'teach_auth_uid';

const TeachRouter = () => {
  // Handle PesaPal iframe redirect immediately before any auth logic
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pesapal_callback') === '1' && window.parent !== window) {
      window.parent.postMessage('pesapal_payment_complete', '*');
    }
  }, []);

  const initParams = new URLSearchParams(window.location.search);
  const isPesapalIframeCallback = initParams.get('pesapal_callback') === '1' && window.parent !== window;

  // Synchronously check if we have a cached auth hint from a previous session.
  // This prevents the redirect-to-login flash during Firebase's async rehydration window.
  const cachedUid = localStorage.getItem(TEACH_AUTH_HINT);

  const [user, setUser] = useState<User | null>(null);
  // Stay in loading state until both the redirect result check AND auth state are resolved
  const [loading, setLoading] = useState(true);
  const redirectChecked = useRef(false);
  const location = useLocation();

  useEffect(() => {
    const auth = getAuth();

    // Step 1: Check for a Google redirect result FIRST (runs on any route the user lands on)
    // This fires before onAuthStateChanged so we never miss a Google sign-in redirect.
    if (!redirectChecked.current) {
      redirectChecked.current = true;
      getRedirectResult(auth)
        .then(async (result) => {
          if (result?.user) {
            const u = result.user;
            // Write auth hint immediately
            localStorage.setItem(TEACH_AUTH_HINT, u.uid);
            // Ensure the workspace exists without overwriting an existing paid subscription.
            const workspaceRef = doc(db, 'teaching_workspaces', u.uid);
            const workspaceSnap = await getDoc(workspaceRef);
            const baseWorkspace = {
              ownerId: u.uid,
              displayName: `${u.displayName || 'My'}'s Workspace`,
              email: u.email,
              createdAt: serverTimestamp(),
            };

            if (workspaceSnap.exists()) {
              await setDoc(workspaceRef, baseWorkspace, { merge: true });
            } else {
              await setDoc(workspaceRef, {
                ...baseWorkspace,
                subscription: { status: 'trial', plan: 'individual_trial' }
              });
            }
            setUser(u);
          }
          // Redirect check complete — proceed to auth state listener
          startAuthListener();
        })
        .catch(() => {
          // No redirect result or error — proceed normally
          startAuthListener();
        });
    }

    function startAuthListener() {
      // Step 2: Enforce LOCAL persistence so sessions survive dev server restarts
      setPersistence(auth, browserLocalPersistence).then(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
          if (currentUser) {
            localStorage.setItem(TEACH_AUTH_HINT, currentUser.uid);
          } else {
            localStorage.removeItem(TEACH_AUTH_HINT);
          }
          setUser(currentUser);
          setLoading(false);
        });
        return () => unsubscribe();
      });
    }
  }, []);

  if (isPesapalIframeCallback) {
    return <div className="h-screen bg-[#020617] text-white flex items-center justify-center text-sm tracking-widest uppercase">Processing payment...</div>;
  }

  // While loading AND no cached hint, show the page loader
  if (loading) {
    return <PageLoader isContentReady={false}>{null}</PageLoader>;
  }

  // If we have a cached hint but Firebase hasn't resolved yet (rehydration window),
  // treat the user as authenticated to prevent redirect flash.
  const isAuthenticated = !!user || !!cachedUid;

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}>
      <AnimatePresence mode="wait">
        <Routes location={location}>
          {/* Landing Page */}
          <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <TeacherLanding />} />

          {/* Public Routes */}
          <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <IndependentLogin />} />
          <Route path="/signup" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <IndependentLogin isSignup={true} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />

          {/* Public Assessment Routes */}
          <Route element={<StudentAssessmentLayout />}>
             <Route path="/assessment/take/:issueId" element={<StudentLanding />} />
             <Route path="/assessment/run/:issueId" element={<AssessmentRunner />} />
          </Route>
          
          {/* Protected Dashboard Routes */}
          <Route path="/dashboard" element={isAuthenticated ? <WorkspaceLayout /> : <Navigate to="/login" replace />}>
            <Route index element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <IndependentDashboard />
              </motion.div>
            } />
            <Route path="lessons" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <LessonPlannerView />
              </motion.div>
            } />
            <Route path="students" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <StudentManagerView />
              </motion.div>
            } />
            <Route path="classes" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <ClassesManagerView />
              </motion.div>
            } />
            <Route path="assessment" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <IndependentAssessmentView />
              </motion.div>
            } />
            <Route path="lessons/new" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <LessonCreateView />
              </motion.div>
            } />
            <Route path="lessons/edit/:id" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <LessonCreateView />
              </motion.div>
            } />
            <Route path="lessons/view/:id" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <LessonReadView />
              </motion.div>
            } />
            <Route path="lessons/course/add" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <AddCourseView />
              </motion.div>
            } />
            <Route path="lessons/course/outline" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <CourseOutlineWriter />
              </motion.div>
            } />
            <Route path="lessons/course/structure" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <CourseOutlineEditor />
              </motion.div>
            } />
            <Route path="lessons/course/content" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <SubTopicContentViewer />
              </motion.div>
            } />
            <Route path="lessons/course/materials" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <CourseMaterialsView />
              </motion.div>
            } />
            <Route path="resources" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <IndependentResourcesView />
              </motion.div>
            } />
            <Route path="research" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <IndependentResearchView />
              </motion.div>
            } />
            <Route path="utilities" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <IndependentUtilitiesView />
              </motion.div>
            } />
            <Route path="utilities/search" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="h-full w-full relative">
                <IndependentSearchUtility />
              </motion.div>
            } />
            <Route path="utilities/pdf-converter" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <IndependentPDFConverterView />
              </motion.div>
            } />
            <Route path="utilities/pdf-converter/create" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <CreatePDFView />
              </motion.div>
            } />
            <Route path="utilities/calculator" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <IndependentCalculatorView />
              </motion.div>
            } />
            <Route path="utilities/ai-calculator" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <IndependentAICalculatorView />
              </motion.div>
            } />
            <Route path="billing" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <IndependentBillingView />
              </motion.div>
            } />
            <Route path="account" element={
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <IndependentAccountView />
              </motion.div>
            } />
          </Route>

          {/* Default Redirect */}
          <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/"} replace />} />
        </Routes>
      </AnimatePresence>
    </GoogleReCaptchaProvider>
  );
};

export default TeachRouter;
